/* eslint-disable react-refresh/only-export-components -- TEMPLATES, CATEGORIES and WheelTemplate are shared data intentionally co-exported from this module */
import React from 'react';
import { Link, useLocaleNavigate } from './LocaleLink';
import MiniWheel from './MiniWheel';

export interface WheelTemplate {
  id: string;
  title: string;
  names: string[];
  description: string;
  /** Grouping for the filter chips. Kept to a handful — a filter row longer
   *  than the result set it filters is worse than no filter at all. */
  category: TemplateCategory;
}

export type TemplateCategory = 'Time' | 'Numbers' | 'Decisions' | 'Games' | 'Learning' | 'Fun';

/** Chip order. Deliberately fixed rather than derived, so the row does not
 *  reshuffle as templates are added. */
export const CATEGORIES: TemplateCategory[] = ['Time', 'Numbers', 'Decisions', 'Games', 'Learning', 'Fun'];

export const TEMPLATES: WheelTemplate[] = [
  {
    id: 'days',
    title: 'Days of the Week',
    names: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
    description: 'Pick a random day',
    category: 'Time',
  },
  {
    id: 'yes-no',
    title: 'Yes or No',
    names: ['Yes', 'No', 'Maybe', 'Ask Again'],
    description: 'Quick decisions',
    category: 'Decisions',
  },
  {
    id: 'rps',
    title: 'Rock Paper Scissors',
    names: ['Rock', 'Paper', 'Scissors'],
    description: 'Classic hand game',
    category: 'Games',
  },
  {
    id: 'months',
    title: 'Months of the Year',
    names: ['January','February','March','April','May','June','July','August','September','October','November','December'],
    description: 'Pick a random month',
    category: 'Time',
  },
  {
    id: 'numbers',
    title: 'Numbers 1–10',
    names: ['1','2','3','4','5','6','7','8','9','10'],
    description: 'Random number pick',
    category: 'Numbers',
  },
  {
    id: 'seasons',
    title: 'Seasons',
    names: ['Spring', 'Summer', 'Autumn', 'Winter'],
    description: 'Which season wins?',
    category: 'Time',
  },
  {
    id: 'colors',
    title: 'Rainbow Colors',
    names: ['Red','Orange','Yellow','Green','Blue','Indigo','Violet'],
    description: 'Pick a random color',
    category: 'Learning',
  },
  {
    id: 'dice',
    title: 'Roll the Dice',
    names: ['1','2','3','4','5','6'],
    description: 'Virtual dice roller',
    category: 'Numbers',
  },
  {
    id: 'truth-dare',
    title: 'Truth or Dare',
    names: ['Truth', 'Dare', 'Truth', 'Dare', 'Pass'],
    description: 'Party game spinner',
    category: 'Games',
  },
  {
    id: 'planets',
    title: 'Planets',
    names: ['Mercury','Venus','Earth','Mars','Jupiter','Saturn','Uranus','Neptune'],
    description: 'Our solar system',
    category: 'Learning',
  },
  {
    id: 'movie-genres',
    title: 'Movie Genres',
    names: ['Action','Comedy','Drama','Horror','Sci-Fi','Romance','Thriller','Animation'],
    description: 'What to watch tonight',
    category: 'Fun',
  },
  {
    id: 'pizza',
    title: 'Pizza Toppings',
    names: ['Pepperoni','Mushrooms','Onions','Peppers','Olives','Extra Cheese','Sausage','Pineapple'],
    description: 'Settle the topping debate',
    category: 'Fun',
  },
  {
    id: 'sports',
    title: 'Sports',
    names: ['Soccer','Basketball','Tennis','Baseball','Swimming','Cycling','Volleyball','Golf'],
    description: 'Pick a sport to play',
    category: 'Fun',
  },
  {
    id: 'languages',
    title: 'Programming Languages',
    names: ['JavaScript','Python','Rust','Go','TypeScript','Java','C++','Swift'],
    description: 'Which lang to learn?',
    category: 'Learning',
  },
  {
    id: 'continents',
    title: 'Continents',
    names: ['Africa','Antarctica','Asia','Australia','Europe','North America','South America'],
    description: 'Next travel destination',
    category: 'Learning',
  },
  {
    id: 'animals',
    title: 'Zoo Animals',
    names: ['Lion','Elephant','Giraffe','Zebra','Gorilla','Panda','Penguin','Crocodile'],
    description: 'Pick a favorite animal',
    category: 'Learning',
  },

  // ── Added to take the gallery from 16 to 48 ──────────────────────────────
  {
    id: 'school-periods',
    title: "School Periods",
    names: ["Period 1", "Period 2", "Period 3", "Period 4", "Period 5", "Period 6", "Period 7", "Period 8"],
    description: "Pick a class period",
    category: 'Time',
  },
  {
    id: 'times-of-day',
    title: "Times of Day",
    names: ["Morning", "Midday", "Afternoon", "Evening", "Night", "Late night"],
    description: "When shall we do it?",
    category: 'Time',
  },
  {
    id: 'weekdays',
    title: "Weekdays Only",
    names: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
    description: "Pick a working day",
    category: 'Time',
  },
  {
    id: 'quarters',
    title: "Quarters",
    names: ["Q1", "Q2", "Q3", "Q4"],
    description: "Pick a quarter",
    category: 'Time',
  },
  {
    id: 'numbers-1-20',
    title: "Numbers 1–20",
    names: ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12", "13", "14", "15", "16", "17", "18", "19", "20"],
    description: "A wider number draw",
    category: 'Numbers',
  },
  {
    id: 'numbers-1-100',
    title: "Numbers 1–100",
    names: ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12", "13", "14", "15", "16", "17", "18", "19", "20", "21", "22", "23", "24", "25", "26", "27", "28", "29", "30", "31", "32", "33", "34", "35", "36", "37", "38", "39", "40", "41", "42", "43", "44", "45", "46", "47", "48", "49", "50", "51", "52", "53", "54", "55", "56", "57", "58", "59", "60", "61", "62", "63", "64", "65", "66", "67", "68", "69", "70", "71", "72", "73", "74", "75", "76", "77", "78", "79", "80", "81", "82", "83", "84", "85", "86", "87", "88", "89", "90", "91", "92", "93", "94", "95", "96", "97", "98", "99", "100"],
    description: "Full hundred draw",
    category: 'Numbers',
  },
  {
    id: 'd20',
    title: "D20",
    names: ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12", "13", "14", "15", "16", "17", "18", "19", "20"],
    description: "Twenty-sided die",
    category: 'Numbers',
  },
  {
    id: 'coin-flip',
    title: "Coin Flip",
    names: ["Heads", "Tails"],
    description: "Two-sided toss",
    category: 'Numbers',
  },
  {
    id: 'percentages',
    title: "Percentages",
    names: ["10%", "20%", "30%", "40%", "50%", "60%", "70%", "80%", "90%", "100%"],
    description: "Pick a percentage",
    category: 'Numbers',
  },
  {
    id: 'even-odd',
    title: "Even or Odd",
    names: ["Even", "Odd"],
    description: "Settle it in one spin",
    category: 'Numbers',
  },
  {
    id: 'yes-no-maybe',
    title: "Yes, No, Maybe",
    names: ["Yes", "No", "Maybe", "Ask again later"],
    description: "With a get-out clause",
    category: 'Decisions',
  },
  {
    id: 'who-pays',
    title: "Who Pays?",
    names: ["Me", "You", "Split it", "Whoever loses the next spin"],
    description: "Settling the bill",
    category: 'Decisions',
  },
  {
    id: 'takeaway',
    title: "Takeaway Night",
    names: ["Pizza", "Burgers", "Sushi", "Curry", "Thai", "Chinese", "Mexican", "Fish & chips"],
    description: "What to order tonight",
    category: 'Decisions',
  },
  {
    id: 'chores',
    title: "Household Chores",
    names: ["Dishes", "Vacuum", "Laundry", "Bins", "Bathroom", "Cooking", "Shopping", "Tidy up"],
    description: "Assign the jobs fairly",
    category: 'Decisions',
  },
  {
    id: 'who-goes-first',
    title: "Who Goes First",
    names: ["Player 1", "Player 2", "Player 3", "Player 4"],
    description: "Start the game",
    category: 'Decisions',
  },
  {
    id: 'workout',
    title: "Workout of the Day",
    names: ["Push-ups", "Squats", "Plank", "Burpees", "Lunges", "Sit-ups", "Jumping jacks", "Rest day"],
    description: "Let the wheel set training",
    category: 'Decisions',
  },
  {
    id: 'breakfast',
    title: "Breakfast Ideas",
    names: ["Toast", "Porridge", "Eggs", "Cereal", "Pancakes", "Fruit", "Yoghurt", "Skip it"],
    description: "End the morning stall",
    category: 'Decisions',
  },
  {
    id: 'truth-dare-forfeit',
    title: "Truth, Dare or Forfeit",
    names: ["Truth", "Dare", "Forfeit", "Pass"],
    description: "Party game spinner",
    category: 'Games',
  },
  {
    id: 'charades',
    title: "Charades Categories",
    names: ["Film", "Book", "Song", "TV show", "Animal", "Famous person", "Action", "Place"],
    description: "What to act out",
    category: 'Games',
  },
  {
    id: 'rps-lizard-spock',
    title: "Rock Paper Scissors Lizard Spock",
    names: ["Rock", "Paper", "Scissors", "Lizard", "Spock"],
    description: "The five-way version",
    category: 'Games',
  },
  {
    id: 'video-game-genres',
    title: "Video Game Genres",
    names: ["Shooter", "RPG", "Strategy", "Platformer", "Racing", "Puzzle", "Horror", "Simulation"],
    description: "What to play next",
    category: 'Games',
  },
  {
    id: 'board-games',
    title: "Board Game Night",
    names: ["Monopoly", "Cluedo", "Scrabble", "Chess", "Catan", "Risk", "Uno", "Jenga"],
    description: "Pick tonight’s game",
    category: 'Games',
  },
  {
    id: 'dares',
    title: "Party Dares",
    names: ["Sing a song", "Tell a joke", "Do an impression", "Dance for 30s", "Speak in an accent", "Reveal a secret", "Do 10 push-ups", "Swap seats"],
    description: "Ready-made dares",
    category: 'Games',
  },
  {
    id: 'school-subjects',
    title: "School Subjects",
    names: ["Maths", "English", "Science", "History", "Geography", "Art", "Music", "PE"],
    description: "Pick a subject",
    category: 'Learning',
  },
  {
    id: 'times-tables',
    title: "Times Tables",
    names: ["2×", "3×", "4×", "5×", "6×", "7×", "8×", "9×", "10×", "11×", "12×"],
    description: "Which table to drill",
    category: 'Learning',
  },
  {
    id: 'punctuation',
    title: "Punctuation Marks",
    names: ["Full stop", "Comma", "Question mark", "Exclamation mark", "Apostrophe", "Colon", "Semicolon", "Quotation marks"],
    description: "Grammar practice",
    category: 'Learning',
  },
  {
    id: 'shapes',
    title: "Shapes",
    names: ["Circle", "Square", "Triangle", "Rectangle", "Pentagon", "Hexagon", "Octagon", "Oval"],
    description: "Name the shape",
    category: 'Learning',
  },
  {
    id: 'oceans',
    title: "Oceans & Seas",
    names: ["Pacific", "Atlantic", "Indian", "Southern", "Arctic", "Mediterranean", "Caribbean", "Red Sea"],
    description: "Geography practice",
    category: 'Learning',
  },
  {
    id: 'instruments',
    title: "Musical Instruments",
    names: ["Piano", "Guitar", "Violin", "Drums", "Flute", "Trumpet", "Cello", "Saxophone"],
    description: "Pick an instrument",
    category: 'Learning',
  },
  {
    id: 'states-of-matter',
    title: "States of Matter",
    names: ["Solid", "Liquid", "Gas", "Plasma"],
    description: "Science starter",
    category: 'Learning',
  },
  {
    id: 'ice-cream',
    title: "Ice Cream Flavours",
    names: ["Vanilla", "Chocolate", "Strawberry", "Mint choc chip", "Cookie dough", "Pistachio", "Salted caramel", "Mango"],
    description: "Pick a scoop",
    category: 'Fun',
  },
  {
    id: 'emojis',
    title: "Mood Check",
    names: ["😀 Great", "🙂 Good", "😐 OK", "😴 Tired", "😤 Stressed", "🤔 Thinking", "🥳 Excited", "😎 Cruising"],
    description: "How is everyone doing?",
    category: 'Fun',
  },
];

interface GallerySectionProps {
  onLoadWheel?: (template: WheelTemplate) => void;
  limit?: number;
  /** Free-text filter, supplied by /gallery?q=… — the target of the WebSite SearchAction. */
  query?: string;
  /** Category filter, supplied by /gallery?cat=… Composed with `query`. */
  category?: string;
}

const GallerySection: React.FC<GallerySectionProps> = ({ onLoadWheel, limit, query, category }) => {
  const navigate = useLocaleNavigate();
  const q = (query ?? '').trim().toLowerCase();
  // Category narrows first, then the text query searches within it — so the two
  // controls compose rather than one silently overriding the other.
  const byCategory = category ? TEMPLATES.filter(t => t.category === category) : TEMPLATES;
  const matched = q
    ? byCategory.filter(t =>
        t.title.toLowerCase().includes(q) ||
        t.description.toLowerCase().includes(q) ||
        t.category.toLowerCase().includes(q) ||
        t.names.some(n => n.toLowerCase().includes(q)))
    : byCategory;
  const displayed = limit ? matched.slice(0, limit) : matched;

  const handleClick = (t: WheelTemplate) => {
    if (onLoadWheel) {
      onLoadWheel(t);
    } else {
      navigate('/', { state: { template: t } });
    }
  };

  return (
    <section>
      {q && (
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4" role="status">
          {displayed.length === 0
            ? <>No templates match &ldquo;{query}&rdquo;. <Link to="/gallery/" className="text-violet-600 dark:text-violet-400 font-semibold hover:underline">Show all templates</Link></>
            : <>{displayed.length} template{displayed.length === 1 ? '' : 's'} matching &ldquo;{query}&rdquo;. <Link to="/gallery/" className="text-violet-600 dark:text-violet-400 font-semibold hover:underline">Clear</Link></>}
        </p>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
        {displayed.map((t) => (
          <button
            key={t.id}
            onClick={() => handleClick(t)}
            className="group flex flex-col items-center gap-3 p-4 bg-white rounded-2xl border border-gray-100 hover:border-violet-200 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 text-center"
          >
            <div className="transition-transform duration-300 group-hover:scale-110 group-hover:rotate-12">
              <MiniWheel names={t.names} size={72} />
            </div>
            <div>
              <p className="text-sm font-bold text-gray-800 leading-tight">{t.title}</p>
              <p className="text-xs text-gray-500 mt-0.5">{t.description}</p>
            </div>
          </button>
        ))}
      </div>
    </section>
  );
};

export default GallerySection;
